<script setup lang="ts">
import { cloneDeep } from 'lodash';
import { useDialogPluginComponent } from 'quasar';
import { computed, ref, toRef } from 'vue';
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import { useI18n } from 'vue-i18n';

import LanguageSwitcher from '../LanguageSwitcher.vue';
import LocalizedInput from '../LocalizedInput.vue';
import LocalizedEditor from '../base/LocalizedEditor.vue';
import LocalizedSelect from '../base/LocalizedSelect.vue';

import { useLanguageValidationErrors } from '@/composables';
import { localizableString, localizableEditorString } from '@/helpers/validation.helper';
import type { Locales, TimelineStepDto } from '@/services/types/api.types';
import { useTimelineStepsStore } from '@/stores/timelineSteps.store';
import { wrapSavingOperation } from '@/helpers/notifications.helper';
import { provideEditLocale } from '@/composables/data-display-utils';

// STORE
const { t } = useI18n();
const timelineStepsStore = useTimelineStepsStore();

// PROPS
const props = defineProps<{
  mode: 'add' | 'edit';
  timelineStep: TimelineStepDto;
  languageEnabled: Locales[];
}>();

// EMITS
defineEmits([...useDialogPluginComponent.emits]);

// CONST
const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } = useDialogPluginComponent();
const initialValues = props.timelineStep;
const { handleSubmit, errors } = useForm({
  initialValues: initialValues,
  validationSchema: yup.object<TimelineStepDto>({
    title: localizableString(props.languageEnabled),
    content: localizableEditorString(props.languageEnabled),
    status: yup.number().required(),
  }),
});
const languageErrors = useLanguageValidationErrors(errors);
const editModel = ref(cloneDeep(props.timelineStep));

const editLocale = provideEditLocale(toRef(() => props.languageEnabled));
const stepStatus = timelineStepsStore.getTimelineStepStatus();

// COMPUTED
const dialogTitle = computed(() =>
  props.mode === 'add' ? t('project-module.timeline.dialogs.create') : t('project-module.timeline.dialogs.edit'),
);

// FUNCTIONS
const onSubmit = handleSubmit(async (values) => {
  await wrapSavingOperation(
    async () => await timelineStepsStore.saveTimelineStep(values),
    () => onDialogOK(),
  );
});
</script>

<template>
  <q-dialog ref="dialogRef" @hide="onDialogHide" persistent>
    <q-card class="q-dialog-plugin" v-if="editModel" style="width: 600px">
      <q-card-section class="row">
        <div class="text-h6">{{ dialogTitle }}</div>
        <q-space />
        <language-switcher
          v-model="editLocale"
          :has-validation-errors="languageErrors"
          :language-enabled="languageEnabled"
        />
      </q-card-section>
      <q-separator />
      <q-card-section>
        <localized-input
          name="title"
          :label="t('global.label.title')"
          :editLocale="editLocale"
          :language_enabled="languageEnabled"
          outlined
        />
        <q-field :label="t('global.label.content')" stack-label borderless>
          <template v-slot:control>
            <localized-editor
              name="content"
              :label="t('global.label.content')"
              class="q-mt-sm full-width"
              :editLocale="editLocale"
              :language_enabled="languageEnabled"
            />
          </template>
        </q-field>
        <localized-select
          name="status"
          :label="t('global.label.status')"
          :editLocale="editLocale"
          :options="stepStatus"
          emit-value
          outlined
          map-options
        />
      </q-card-section>
      <q-separator />
      <q-card-actions align="right">
        <q-btn color="primary" flat :label="t('global.cancel')" @click="onDialogCancel" />
        <q-btn color="primary" :label="t('global.save')" @click="onSubmit" />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>
